import type { GameEvent } from './types';

export class AudioGarden {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted: boolean;
  private haptics: boolean;
  private unavailable = false;

  constructor(muted: boolean, haptics: boolean) {
    this.muted = muted;
    this.haptics = haptics;
  }

  async activate(): Promise<boolean> {
    if (this.unavailable) return false;
    try {
      if (!this.context) {
        this.context = new AudioContext({ latencyHint: 'interactive' });
        this.master = this.context.createGain();
        this.master.gain.value = this.muted ? 0 : .52;
        this.master.connect(this.context.destination);
      }
      if (this.context.state === 'suspended') await this.context.resume();
      return true;
    } catch {
      this.unavailable = true;
      return false;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) this.master.gain.setTargetAtTime(muted ? 0 : .52, this.context.currentTime, .025);
  }

  setHaptics(enabled: boolean): void { this.haptics = enabled; }

  handle(event: GameEvent): void {
    if (this.muted || !this.context || !this.master || this.context.state !== 'running') return;
    switch (event.type) {
      case 'launch':
        this.tone(116 + event.strength * 22, .14, 'triangle', .17, 1.7);
        this.noise(.055, .045, 620);
        this.vibrate(12);
        break;
      case 'pulse':
        this.tone(event.remaining === 1 ? 196 : 164, .18, 'sine', .16, 1.55);
        this.tone(event.remaining === 1 ? 294 : 246, .12, 'triangle', .07, 1.18, .025);
        this.vibrate(18);
        break;
      case 'petal': {
        const scale = [0, 2, 4, 7, 9, 12, 14, 16];
        const semitone = scale[Math.min(scale.length - 1, event.combo - 1)] ?? 0;
        this.tone(261.63 * Math.pow(2, semitone / 12), .24, 'sine', .13, 1.08);
        this.vibrate(event.combo > 2 ? [8, 20, 8] : 8);
        break;
      }
      case 'bumper':
        this.tone(92 + event.intensity * 48, .11, 'square', .06 + event.intensity * .07, 2.2);
        this.noise(.04, .035 + event.intensity * .04, 380);
        this.vibrate(Math.round(8 + event.intensity * 14));
        break;
      case 'surface':
        this.tone(86 + event.intensity * 35, .075, 'triangle', .035 + event.intensity * .045, .72);
        this.noise(.028, .022 + event.intensity * .025, 290);
        break;
      case 'goal-locked':
        this.tone(174, .11, 'triangle', .1, .72);
        this.tone(146, .14, 'triangle', .08, .68, .07);
        break;
      case 'goal-rejected':
        this.tone(138, .18, 'sawtooth', .08, .48);
        this.vibrate([24, 30, 16]);
        break;
      case 'fail':
        this.tone(146, .36, 'triangle', .12, .46);
        this.noise(.18, .05, 260);
        this.vibrate([28, 45, 28]);
        break;
      case 'complete':
        [0, 4, 7, 12].forEach((semitone, index) => this.tone(220 * Math.pow(2, semitone / 12), .55 - index * .04, 'sine', .105, 1.02, index * .09));
        this.vibrate([12, 35, 12, 35, 25]);
        break;
    }
  }

  private tone(frequency: number, duration: number, type: OscillatorType, volume: number, endRatio: number, delay = 0): void {
    if (!this.context || !this.master) return;
    const start = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, frequency * endRatio), start + duration);
    gain.gain.setValueAtTime(.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + .008);
    gain.gain.exponentialRampToValueAtTime(.0001, start + duration);
    oscillator.connect(gain); gain.connect(this.master);
    oscillator.start(start); oscillator.stop(start + duration + .02);
  }

  private noise(duration: number, volume: number, cutoff: number): void {
    if (!this.context || !this.master) return;
    const length = Math.max(1, Math.floor(this.context.sampleRate * duration));
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.sin(i * 12.9898) * Math.cos(i * 4.1414);
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    filter.type = 'lowpass'; filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(volume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(.0001, this.context.currentTime + duration);
    source.buffer = buffer; source.connect(filter); filter.connect(gain); gain.connect(this.master);
    source.start();
  }

  private vibrate(pattern: number | number[]): void {
    if (this.haptics && 'vibrate' in navigator) navigator.vibrate(pattern);
  }

  dispose(): void {
    if (this.context) void this.context.close();
    this.context = null;
    this.master = null;
  }
}
